import React, { useState } from "react";
import {
  Alert,
  Button,
  Group,
  Stack,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useCreate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";

import type { Restaurant } from "../../types";

export function RestaurantCreate() {
  const navigate = useNavigate();
  const { mutateAsync, isLoading } = useCreate<Restaurant>();

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [querySetupText, setQuerySetupText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      setError(null);

      // Parse JSON only if provided (and show a friendly error if invalid)
      let querySetup: unknown | null = null;
      if (querySetupText.trim()) {
        try {
          querySetup = JSON.parse(querySetupText);
        } catch {
          setError("Query setup must be valid JSON.");
          return;
        }
      }

      const res = await mutateAsync({
        resource: "restaurants",
        values: {
          name: name.trim() || null,
          description: description.trim() || null,
          adminNotes: adminNotes.trim() || null,
          querySetup,
        },
      });

      navigate(`/admin/restaurants/show/${res.data.id}`);
    } catch (e: any) {
      setError(e?.message ?? "Failed to create restaurant");
    }
  };

  return (
    <Stack gap="md">
      <Title order={2}>Create restaurant</Title>

      {error ? <Alert color="red">{error}</Alert> : null}

      <TextInput
        label="Name"
        placeholder="e.g. Kosho Sushi"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <Textarea
        label="Description"
        placeholder="Short description shown in admin"
        minRows={3}
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />

      <Textarea
        label="Admin notes"
        description="Included in menu chat context. These are instructions for the AI to follow when responding to users."
        placeholder="Internal notes"
        minRows={4}
        value={adminNotes}
        onChange={(e) => setAdminNotes(e.currentTarget.value)}
      />

      <Textarea
        label="Query setup (JSON)"
        description="Optional. Must be valid JSON if provided."
        placeholder='{ "configuration": "..." }'
        minRows={6}
        value={querySetupText}
        onChange={(e) => setQuerySetupText(e.currentTarget.value)}
      />

      <Group justify="flex-end">
        <Button variant="default" onClick={() => navigate("/admin/restaurants")}> 
          Cancel
        </Button>
        <Button loading={isLoading} onClick={handleSubmit}>
          Create
        </Button>
      </Group>
    </Stack>
  );
}
